import {
    Editor,
    Range,
    Selection,
} from 'slate';

import {
    IAnnotationQuote,
    IAnnotationTextAnchor,
} from '../interfaces';

const ANNOTATION_TEXT_ANCHOR_CONTEXT_CHAR_COUNT = 32;

/**
 * Slate selections are mutable-like data that we pass through several optimistic
 * and persistence layers. Cloning keeps later editor changes from mutating the
 * quote payload we are about to save.
 */
function cloneSelection(selection: Selection | null | undefined): Selection | null {
    if (!selection) {
        return null;
    }

    return {
        anchor: {
            path: [...selection.anchor.path],
            offset: selection.anchor.offset,
        },
        focus: {
            path: [...selection.focus.path],
            offset: selection.focus.offset,
        },
    };
}

/**
 * Reads editor text defensively so quote creation can fail soft while the editor
 * is still reconciling collaborative updates.
 */
function getEditorText(editor: Editor, selection: Exclude<Selection, null>): string {
    try {
        return Editor.string(editor, selection);
    } catch (_error) {
        return '';
    }
}

/**
 * Builds a small before/after text window around one saved selection. That
 * context gives migrations a stable way to find the quote again after later
 * edits shift raw node coordinates.
 */
function buildAnnotationTextAnchor({
    editor,
    selection,
    text,
}: {
    editor: Editor,
    selection: Selection | null | undefined,
    text: string,
}): IAnnotationTextAnchor {
    const nextText = typeof text === 'string' ? text : '';
    if (!selection) {
        return {
            text: nextText,
            prefixText: '',
            suffixText: '',
        };
    }

    try {
        const [startPoint, endPoint] = Range.edges(selection);
        const prefixStartPoint = Editor.before(editor, startPoint, {
            distance: ANNOTATION_TEXT_ANCHOR_CONTEXT_CHAR_COUNT,
            unit: 'character',
        }) || Editor.start(editor, []);
        const suffixEndPoint = Editor.after(editor, endPoint, {
            distance: ANNOTATION_TEXT_ANCHOR_CONTEXT_CHAR_COUNT,
            unit: 'character',
        }) || Editor.end(editor, []);

        return {
            text: nextText,
            prefixText: getEditorText(editor, {
                anchor: prefixStartPoint,
                focus: startPoint,
            }),
            suffixText: getEditorText(editor, {
                anchor: endPoint,
                focus: suffixEndPoint,
            }),
        };
    } catch (_error) {
        return {
            text: nextText,
            prefixText: '',
            suffixText: '',
        };
    }
}

/**
 * Creates one persistence-ready annotation quote payload with both coordinate
 * selections and text anchors.
 */
function buildAnnotationQuote({
    editor,
    id,
    timestamp,
    selections,
    texts,
}: {
    editor: Editor,
    id: string,
    timestamp: number,
    selections: Selection[],
    texts: string[],
}): IAnnotationQuote {
    const nextSelections = selections
        .map((selection) => cloneSelection(selection))
        .filter((selection): selection is Selection => !!selection);
    const nextTexts = texts.map((text) => `${text || ''}`);

    return {
        id,
        timestamp,
        selections: nextSelections,
        texts: nextTexts,
        textAnchors: nextTexts.map((text, index) => buildAnnotationTextAnchor({
            editor,
            selection: nextSelections[index],
            text,
        })),
    };
}

export default buildAnnotationQuote;
